import React from 'react';
import { Input } from 'antd';
import { LinkOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AppButton } from '../common/AppButton';
import { AppTag } from '../common/AppTag';
import { DecidedResultLine } from '../decidedPriceTable/DecidedPriceTable';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SPACING } from '../../constants/constants';
import { displaySellerCode } from '../../types/card';
import type { DecidedRowResult, DecisionListItem, DecisionMarketItems } from '../../types/decidedPrice';
import { naverShoppingUrl, roundTo10, safeHttpUrl, type PendingSave } from '../../utils/decidedPrice';

// ─── 판매가 결정 — 오른쪽 판매처 패널(감사 card-controltower#341) ─────────────────────────────────────────────
//
// 고른 카드의 이미지 · 코드 · 수집일시 · PriceHub 가격 · 빠른 설정(최저 · 평균 · 중간) · 판매처 목록(누르면 그 가격이 입력칸에).
// 넓은 화면은 표 오른쪽에 붙고, 좁은 화면은 아래 시트(AppDrawer) 안에 그대로 들어간다. 저장은 표의 줄과 같은 상태를 쓴다.

const won = (n: number | null | undefined): string => (n == null ? '-' : `${n.toLocaleString('ko-KR')}원`);

export interface MarketState {
  loading: boolean;
  /** 404 가 아닌 실패(PriceHub · 네트워크). */
  error:   boolean;
  /** null = PriceHub 에 없음(404) 또는 아직 안 받음. */
  data:    DecisionMarketItems | null;
}

export interface MarketSellerPanelProps {
  item:      DecisionListItem;
  market:    MarketState;
  /** 'PriceHub 가격' 또는 '광주 가격' 처럼 hubPrice 의 이름. */
  hubLabel:  string;
  draft:     string | undefined;
  pending:   PendingSave | undefined;
  result:    DecidedRowResult | undefined;
  saving:    boolean;
  readOnly:  boolean;
  /** 줄 저장을 막는 사유(일괄 저장 중 · 다른 카드 저장 중). */
  saveLock:  string | undefined;
  /** 서버가 같은 요청을 처리 중(409) — 다시 시도를 잠근다. */
  busy:      boolean;
  phone:     boolean;
  onPick:    (price: number) => void;
  onDraft:   (raw: string) => void;
  onSave:    () => void;
  onRetryMarket: () => void;
  /** 결과 불명 요청을 버리고 새로 결정(확인 창은 화면이). */
  onResetPending: () => void;
  /** 결과 칩의 "매장 가격 요청에서 보기"(없으면 칩만). */
  onGoRequests?: () => void;
}

export const MarketSellerPanel: React.FC<MarketSellerPanelProps> = ({
  item, market, hubLabel, draft, pending, result, saving, readOnly, saveLock, busy, phone,
  onPick, onDraft, onSave, onRetryMarket, onResetPending, onGoRequests,
}) => {
  const t = useThemeTokens();
  const code = displaySellerCode(item.sellerProductCode);
  const summary = market.data?.summary ?? item.market;
  const collectedAt = market.data?.collectedAt ?? item.market?.collectedAt ?? null;
  const locked = !!pending;
  const unknown = locked && !saving;
  const inputValue = locked ? String(pending!.price) : (draft ?? '');
  const ctrlHeight = phone ? 44 : 32;
  const pickDisabled = readOnly || locked || saving;
  const saveDisabledReason = readOnly ? '보기 권한 계정은 저장할 수 없습니다'
    : saveLock && !saving ? saveLock
      : busy ? '서버가 같은 요청을 처리 중입니다 — 잠시 뒤 다시 시도하세요'
        : !locked && !inputValue ? '판매가를 입력하세요' : undefined;

  const quick: { key: string; label: string; value: number | null }[] = [
    { key: 'min', label: '최저', value: summary?.min ?? null },
    { key: 'avg', label: '평균', value: summary?.avg != null ? roundTo10(summary.avg) : null },
    { key: 'median', label: '중간', value: summary?.median != null ? roundTo10(summary.median) : null },
  ];

  const sectionLabel = (text: string, hint?: string) => (
    <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, fontWeight: FONT_WEIGHT.semibold }}>
      {text}{hint && <span style={{ color: t.textMuted, fontWeight: FONT_WEIGHT.regular }}> — {hint}</span>}
    </div>
  );

  const sellerList = () => {
    if (market.loading) return <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>판매처 목록 불러오는 중…</div>;
    if (market.error) {
      return (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, fontSize: FONT_SIZE.xs, color: t.danger, lineHeight: LINE_HEIGHT.relaxed }}>
          판매처 목록을 불러오지 못했습니다(PriceHub · 네트워크 — 카드가 없는 것과는 다릅니다).
          <AppButton size="sm" icon={<ReloadOutlined />} onClick={onRetryMarket}>다시 시도</AppButton>
        </div>
      );
    }
    const items = market.data?.items ?? [];
    if (items.length === 0) return <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>판매처 목록 없음</div>;
    return (
      <div role="list" aria-label="판매처 목록" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
        {items.map((s, i) => {
          const href = safeHttpUrl(s.url);
          return (
            <div key={`${s.shop ?? ''}-${i}`} role="listitem" style={{ display: 'flex', alignItems: 'stretch', gap: SPACING.xxs }}>
              <button
                type="button"
                disabled={pickDisabled || s.price == null}
                onClick={() => { if (s.price != null) onPick(s.price); }}
                aria-label={`${s.shop ?? '판매처'} ${won(s.price)} 입력칸에 넣기`}
                title={pickDisabled ? undefined : '누르면 이 가격이 입력칸에 들어갑니다'}
                style={{
                  flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: SPACING.xs, textAlign: 'left',
                  border: `1px solid ${t.border}`, background: t.bgSurface, borderRadius: RADIUS.md,
                  padding: `${SPACING.xxs}px ${SPACING.xs}px`, minHeight: phone ? 56 : 52,
                  cursor: pickDisabled || s.price == null ? 'not-allowed' : 'pointer', color: t.textPrimary,
                }}
              >
                <div style={{ width: 36, height: 48, borderRadius: RADIUS.sm, background: t.bgPlaceholder, flexShrink: 0, overflow: 'hidden' }}>
                  {s.imageUrl && (
                    <img src={s.imageUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.shop ?? '-'}
                  </div>
                  <div title={s.title ?? undefined} style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.title ?? ''}
                  </div>
                  <div style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.bold, color: t.success, fontVariantNumeric: 'tabular-nums' }}>
                    {won(s.price)}
                  </div>
                </div>
              </button>
              {href && (
                <a href={href} target="_blank" rel="noopener noreferrer" aria-label={`${s.shop ?? '판매처'} 상품 열기`} title="판매처 상품 열기"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: phone ? 44 : 32, flexShrink: 0,
                    border: `1px solid ${t.border}`, borderRadius: RADIUS.md, color: t.textSecondary, background: t.bgRaised,
                  }}>
                  <LinkOutlined />
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="market-seller-panel" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
      <div style={{ display: 'flex', gap: SPACING.sm }}>
        <div style={{ width: phone ? 96 : 120, flexShrink: 0 }}>
          {item.imageUrl
            ? <img src={item.imageUrl} alt={item.productName} style={{ width: '100%', borderRadius: RADIUS.md, border: `1px solid ${t.border}`, display: 'block' }} />
            : <div style={{ width: '100%', aspectRatio: '63 / 88', borderRadius: RADIUS.md, background: t.bgPlaceholder, border: `1px solid ${t.border}` }} />}
        </div>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
          <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.bold, color: t.textPrimary, lineHeight: LINE_HEIGHT.snug }}>
            {item.productName}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, flexWrap: 'wrap' }}>
            <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>{code || '코드 없음'}</span>
            <AppTag variant="rarity" code={item.rarity} />
            {item.isManual && <AppTag variant="manual" />}
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>
            수집일시 {collectedAt ? dayjs(collectedAt).format('YYYY.MM.DD HH:mm') : '-'}
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
            네이버 가격 <b style={{ color: t.textPrimary }}>{won(item.currentPrice)}</b>
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
            {hubLabel} <b style={{ color: t.textPrimary }}>{won(item.hubPrice)}</b>
          </div>
          {summary && (
            <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>
              타 샵 시세 {won(summary.min)} ~ {won(summary.max)} · {summary.shopCount ?? 0}곳
            </div>
          )}
        </div>
      </div>

      <a
        href={naverShoppingUrl(item.productName)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACING.xxs, minHeight: ctrlHeight,
          border: `1px solid ${t.border}`, borderRadius: RADIUS.md, color: t.accent, background: t.bgRaised,
          fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold,
        }}
      >
        <SearchOutlined /> 네이버쇼핑에서 검색
      </a>

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
        {sectionLabel('판매가', unknown ? '결과 불명 — 같은 요청으로만 다시 시도' : undefined)}
        <div style={{ display: 'flex', gap: SPACING.xxs }}>
          <Input
            aria-label="패널 판매가"
            inputMode="numeric"
            value={inputValue}
            disabled={readOnly || locked || saving}
            placeholder={item.currentPrice != null ? String(item.currentPrice) : '가격'}
            onChange={(e) => onDraft(e.target.value)}
            onPressEnter={() => { if (!saveDisabledReason && !saving) onSave(); }}
            style={{ textAlign: 'right', height: ctrlHeight, flex: 1, minWidth: 0 }}
          />
          <AppButton
            variant={unknown ? 'ghost-danger' : 'primary'}
            size={phone ? 'lg' : 'md'}
            loading={saving}
            disabled={!!saveDisabledReason}
            disabledReason={saveDisabledReason}
            onClick={onSave}
          >
            {unknown ? '다시 시도' : '저장'}
          </AppButton>
        </div>
        <DecidedResultLine
          t={t} result={result} pending={pending} saving={saving} phone={phone} onGoRequests={onGoRequests}
          onReset={!readOnly && !busy && !saveLock && unknown ? onResetPending : undefined}
        />
        <div role="group" aria-label="빠른 설정" style={{ display: 'flex', gap: SPACING.xxs, flexWrap: 'wrap' }}>
          {quick.map((q) => (
            <AppButton
              key={q.key}
              size={phone ? 'lg' : 'sm'}
              disabled={pickDisabled || q.value == null}
              onClick={() => { if (q.value != null) onPick(q.value); }}
            >
              {q.label} {q.value != null ? won(q.value) : '-'}
            </AppButton>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
        {sectionLabel('판매처 목록', '누르면 그 가격이 입력칸에')}
        {sellerList()}
      </div>
    </div>
  );
};
